import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of, catchError, mergeMap, tap } from 'rxjs'
import {
  fetch_company_setting_preferences_data,
  fetch_company_setting_preferences_data_success,
  initialize_failed,
  initialize_route_after_success,
  initialize_success,
  start_initializing
} from '../action/initialize.action'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { show_error_action } from '../action/common/common.action'
import { GlobalConstants } from '../../constants/global-constants'
import { CommonService } from '../../services/common/common.service'
import { DynamicComponentLoaderService } from 'src/app/services/component-loader/dynamic-component-loader.service'
import { AuthType } from 'src/app/models/model'

@Injectable()
export class InitializeEffects {
  constructor(
    private actions$: Actions,
    private initializeService: InitializeService,
    private cs: CommonService,
    private dynamicComponentLoaderService: DynamicComponentLoaderService
  ) {}

  start_initialize$ = createEffect(() => {
    let comingFromLogin = false
    return this.actions$.pipe(
      ofType(start_initializing),
      mergeMap((action) => {
        comingFromLogin = action.comingFromLogin
        return this.initializeService.getInitialSettingsData().pipe(
          mergeMap((response) => {
            const initializeAction = initialize_success({
              initializeData: response,
              comingFromLogin
            })

            const routeAfterSuccessAction = initialize_route_after_success({ initializeData: response })

            return [initializeAction, routeAfterSuccessAction]
          }),
          // Fixed (was previously a known bug, see docs/PROGRESS.md): the catchError used to only
          // wrap the API call, so its initialize_failed(error) fallback flowed into the mergeMap
          // above as if it were a real response, getting silently re-wrapped into a bogus
          // initialize_success instead of ever reaching the store as initialize_failed. Moving
          // catchError after the success-wrapping mergeMap means an API error now bypasses that
          // mergeMap entirely and emits initialize_failed(error) on its own, as intended.
          catchError((error: any) => of(initialize_failed(error)))
        )
      })
    )
  })

  fetch_company_setting_preferences_data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetch_company_setting_preferences_data),
      mergeMap((action) =>
        this.initializeService.invokeFetchCompanySettingPreferences(action.groupId).pipe(
          mergeMap((companySettingPreferences) => [
            fetch_company_setting_preferences_data_success({ companySettingPreferences })
          ]),
          catchError((error: any) => of(show_error_action({ data: null, error: error })))
        )
      )
    )
  )

  loginRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(initialize_route_after_success),
        tap((action) => {
          if (
            action?.initializeData?.user?.passwordAutoGenerated &&
            AuthType.PASSWORD === action?.initializeData?.authType
          ) {
            this.dynamicComponentLoaderService.loadComponent(GlobalConstants.DYNAMIC_MODEL.RESET_PASSWORD.key)
          }

          let url = GlobalConstants.ROUTING.DASHBOARD
          if (action?.initializeData?.newJoineeActivityDetails) {
            this.dynamicComponentLoaderService.loadComponent(
              GlobalConstants.DYNAMIC_MODEL.NEW_JOINEE_WORKFLOW_NOTIFICATION_POPUP.key
            )
          }

          if (action?.initializeData?.pendingQueriesCount > 0) {
            this.dynamicComponentLoaderService.loadComponent(
              GlobalConstants.DYNAMIC_MODEL.PENDING_FROM_EMPLOYEE_NOTIFICATION_POPUP.key
            )
          }

          if (action?.initializeData?.feedbackList) {
            const initiatedFeedback = action.initializeData.feedbackList.find(
              (feedbackDetail) => feedbackDetail.uiStatus === 'NOT-INTERACTED'
            )

            if (initiatedFeedback) {
              let initiatedFeedbackId = initiatedFeedback.id
              url = `/${GlobalConstants.ROUTING.FEEDBACK}/${initiatedFeedbackId}`
            }
          }

          this.cs.routeTo(url)
        })
      )
    },
    { dispatch: false }
  )
}
